import React, { FC, useEffect, useRef, useState } from 'react';

import { inputStateClass } from './fieldState';

/** A perspective transform is a 4x4 matrix. */
const EXPECTED_VALUES = 16;
const VALUES_PER_LINE = 4;

interface PerspectiveTransformFieldProps {
  value: number[] | null;
  onChange: (value: number[] | null) => void;
  label: string;
}

interface ParsedTransform {
  values: number[];
  hasInvalidToken: boolean;
  hasEmptySlot: boolean;
}

/**
 * `1,,2` — two commas with nothing between them — is a value lost in a copy-paste, not a
 * separator. Collapsing it would quietly shift every later number into the wrong cell of
 * the matrix, so it is rejected rather than tidied away.
 *
 * Only a gap *between* values counts. A leading or trailing comma encloses nothing and
 * loses nothing, and runs of whitespace stay a single separator — wrapped and indented
 * pastes are normal. Anything that genuinely dropped a value off either end still fails
 * the count.
 */
const hasEmptySlot = (raw: string): boolean =>
  /,\s*,/.test(raw.replace(/[[\]]/g, ''));

/**
 * Pastes arrive comma-separated, newline-separated, bracketed, or with stray whitespace —
 * so any mix of those separators is accepted. Mirrors the server's parser for immediate
 * feedback; the server re-validates at commit and remains the authority.
 */
const parseTransform = (raw: string): ParsedTransform => {
  const tokens = raw
    .replace(/[[\]]/g, '')
    .split(/[\s,]+/)
    .filter(token => token !== '');
  const values = tokens.map(Number);
  return {
    values: values.filter(value => Number.isFinite(value)),
    hasInvalidToken: values.some(value => !Number.isFinite(value)),
    hasEmptySlot: hasEmptySlot(raw),
  };
};

const format = (values: number[]): string => {
  const lines: string[] = [];
  for (let index = 0; index < values.length; index += VALUES_PER_LINE) {
    lines.push(values.slice(index, index + VALUES_PER_LINE).join(', '));
  }
  return lines.join('\n');
};

export const PerspectiveTransformField: FC<PerspectiveTransformFieldProps> = ({
  value,
  onChange,
  label,
}) => {
  /*
   * The raw text is local state rather than form state: the reviewer needs to see exactly
   * what they typed while it is still half-finished, and the form only ever holds parsed
   * numbers.
   */
  const [raw, setRaw] = useState(value ? format(value) : '');
  /*
   * What this field last handed up. A half-typed value is handed up as null — `1,,2` must
   * not reach the draft — and without this the null would come straight back as "adopt an
   * empty value" and wipe the text mid-edit.
   */
  const lastEmitted = useRef<string | null>(null);

  useEffect(() => {
    // adopt a value that arrived from outside (a fresh parse, or coming back from preview)
    const incoming = (value ?? []).join(',');
    if (lastEmitted.current === incoming) {
      return;
    }
    setRaw(value ? format(value) : '');
  }, [value]);

  const parsed = parseTransform(raw);
  const { values, hasInvalidToken } = parsed;
  const isEmpty = raw.trim() === '';
  const isValid =
    !hasInvalidToken &&
    !parsed.hasEmptySlot &&
    values.length === EXPECTED_VALUES;
  // required, so an empty one is as blocking as a wrong one and says so in the same colour
  const state = isValid ? 'neutral' : 'invalid';

  const handleChange = (next: string): void => {
    setRaw(next);
    const nextParsed = parseTransform(next);
    // an ambiguous paste must not reach the draft, even if it happens to count to 16;
    // anything else is handed up as it parses, so validation can say "13 — expecting 16"
    const emitted =
      next.trim() === '' || nextParsed.hasEmptySlot ? null : nextParsed.values;
    lastEmitted.current = (emitted ?? []).join(',');
    onChange(emitted);
  };

  const handleBlur = (): void => {
    if (isValid) {
      setRaw(format(values));
    }
  };

  const status = isEmpty
    ? 'Required — paste the 16 values'
    : parsed.hasEmptySlot
    ? 'Two commas with nothing between them — a value is missing'
    : hasInvalidToken
    ? 'Contains a value that is not a number'
    : values.length === EXPECTED_VALUES
    ? `${EXPECTED_VALUES} values ✓`
    : `${values.length} values — expecting ${EXPECTED_VALUES}`;

  const statusClass = isValid ? 'text-green-dark' : 'text-red-dark';

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-semibold">{label}</p>
        <p className={`text-sm font-semibold ${statusClass}`}>{status}</p>
      </div>
      <textarea
        className={`mt-1 w-full rounded p-2 font-mono text-sm outline-none ${inputStateClass(
          state
        )} ${state === 'neutral' ? 'border border-primary-250 bg-white' : ''}`}
        rows={4}
        value={raw}
        placeholder="Paste the 16 values — commas, spaces or new lines all work"
        onChange={event => handleChange(event.target.value)}
        onBlur={handleBlur}
      />
    </div>
  );
};
